import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {environment} from 'src/environments/environment';
import {catchError, delay, filter, map, take, tap} from "rxjs/operators";

// models
import {
  AnnualIncome,
  Brand,
  ConfigSetting,
  Country,
  FalconSetting,
  IncomeSource,
  Occupation,
  OnboardingRequest,
  PasswordPolicy,
  Product,
  State
} from '../model/models';
import {Customer} from 'ApiGen/CustomerVerificationV2/model/models';
import {NGXLogger} from "ngx-logger";

// internal services
import {AppSettingsService} from '../services/appsettings.service';
import {NotificationService} from '../services/notification.service';
import {ShoppingCartService} from '../services/shopping-cart.service';
import {SessionStorageService} from "../services/session-storage.service";

// ReST Services
import {ErrorService} from "../services/error.service";
import {
  ApplicantControllerService,
  ApplicantRequest,
  ApplicationDetailLog,
  ApplicationLogDetailsRequest,
  ApplicationLogsControllerService,
  ApplicationLogsRequest, ApplicationLogStepRequest,
  ApplicationProduct,
  ApplicationProductRequest,
  ApplicationPromotionControllerService,
  ApplicationPromotionDetails,
  JointApplicantRequest,
  Log,
  SecureApplicationControllerService,
  SecureApplicationValidityResponse,
  SecureApplicationVerificationRequest,
  SecureApplicationVerificationResponse,
  Step
} from "leia-api";

import {
  BranchControllerService,
  OnboardingApiControllerService,
  ProductControllerService,
  ProductDetails,
  ProductTypeDetails,
  ProductView,
  PromotionControllerService,
  PromotionVerificationRequest,
  PromotionVerificationResponse,
  SettingDetails
} from "falcon-api";
import {Q2caliperService} from "../services/q2caliper.service";
import {ThemeService} from "../../core/services/theme.service";
import {
  AddressVerificationControllerService,
  AddressVerificationRequest,
  AddressVerificationResponse,
  CustomerControllerService,
  EligibilityResponse,
  EnrollmentResponse,
  EvaluateRequest,
  KycControllerService,
  SalesForceAlloyObject,
  ServiceResponse
} from "skyhopper-api";
import {UtilitiesService} from "../services/utilities.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ApplicationDialog, DialogsService} from "../services/dialogs.service";
import {
  OnboardingExceptionResponse,
  OnboardingService,
  OnboardingSuccessfulResponse
} from "../services/onboarding.service";
import {SuccessConfiguration} from "../model/success-configuration";
import {
  BASK_URL_CHECK,
  BRANCH_ID,
  CUSTOMER_SUPPORT_EMAIL_KEY,
  CUSTOMER_SUPPORT_PHONE_KEY,
  EMPLOYEE_CHECKING_PRODUCT_CODE,
  EMPLOYEE_SAVINGS_PRODUCT_CODE,
  ERROR_PATH,
  ERROR_TITLE_KEY,
  JOINT_APPLICATION_DETAILS_KEY,
  LOCAL_SESSION_ID,
  MINOR_SAVINGS_PRODUCT_NAME,
  RC_ACCOUNT_RULES_ERROR_KEY, RC_APPLICANT_INFO_MISMATCH_ERROR_KEY,
  RC_ERROR_PROPS_KEY, RC_HYSA_ACCOUNT_RULES_ERROR_KEY,
  RC_JOINT_ACCOUNT_ERROR_KEY,
  RC_PRODUCT_ELIGIBILITY_ERROR_KEY,
  RC_STATUS_U_ERROR_PROPS_KEY,
  RC_UNDER_REVIEW_PROPS_KEY,
  SUCCESS_CANCEL_URL_KEY,
  SUCCESS_HOW_TO_CONTACT_KEY,
  SUCCESS_JOINT_MESSAGE_KEY,
  SUCCESS_LOGIN_URL_KEY,
  SUCCESS_MESSAGE_KEY,
  SUCCESS_NEXT_STEPS_KEY,
  SUCCESS_TITLE_KEY,
  SUPPRESS_ACCOUNT_DETAILS,
  SUPPRESS_ACCOUNT_DETAILS_VALUE
} from "../constants";
import {ReferenceConfiguration, ReferenceProperties} from "../model/reference-configuration";
import {CodeVerificationService} from "../services/code-verification.service";
import {SignupService} from "src/app/signup/service/signup.service";
import {OnboardingApplicant} from "../model/onboarding-applicant.model";
import {CoreService} from "../services/core.service";
import {ProductType} from "../../signup/models/product-type";
import {ApplicationLogMappingService} from "../services/application-log-mapping.service";
import {ReferenceCode} from "../../process/models/referenceCode";
import {DeviceType} from '../model/device-type';
import {DeviceDetectorService} from "ngx-device-detector";
import {AbandonReason} from "../model/abandon-reason";
import {ApplicationChannel} from "../model/application-channel";
import {CookieService} from "ngx-cookie-service";
import {BrandType} from "../../signup/models/brand-type";
import {MediaObserver} from "@angular/flex-layout";
import {JointAccountSecureApplicationResponse} from "leia-api/model/jointAccountSecureApplicationResponse";
import {datadogRum} from "@datadog/browser-rum";
import {ApplicationType} from "../model/application-type.enum";
import {Promotion} from "../model/promotion";
import NameEnum = Step.NameEnum;
import StateEnum = Step.StateEnum;
import StatusEnum = ApplicationLogsRequest.StatusEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import {SettingsEnum} from "../model/settings.enum";
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import {Configuration} from '../configuration.enum';
import {SecureApplicationMetadata} from "leia-api/model/secureApplicationMetadata";
import { Account, AccountType, SelectedAccount } from 'src/app/signup/account-selector/account-selector.component';
import { EligibilityStatus } from '../services/eligibility.service';
import { AccountMediatorService } from '../services/account-mediator.service';
import { ProductEligibilityValidator } from 'src/app/signup/account-selection/product-eligibility.validator';
import { SecureApplicationReasonEnum } from '../model/secure-application-reason.enum';
import { FormArray, FormGroup } from '@angular/forms';
import { ActivatedRouteSnapshot } from '@angular/router';
import { SectionStatusService } from 'src/app/status-tracker/section-status.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Facade service class. This class is a facade layer to all the services.
 */
export class FacadeService {
  public isInJointMaintenanceFlow$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public globalSettings$!: Observable<SettingDetails[]>;
  public globalSettingsLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public brandProductSettingsLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public notEnrolledInOLB: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
  public shoppingCartOnboardingRequest$: BehaviorSubject<OnboardingRequest> = new BehaviorSubject(new OnboardingRequest());

  productSettings: Map<string, Map<string, unknown>> = new Map();

  //isInAuthenticatedFlow$ has been expanded : [a, b] => a = isInAuthenticatedFlow, b = isBeingResumed.
  public isInAuthenticatedFlow$: BehaviorSubject<boolean[]> = new BehaviorSubject<boolean[]>([false,false]);
  private _secureApplicationReason$: BehaviorSubject<SecureApplicationReasonEnum | undefined> = new BehaviorSubject<SecureApplicationReasonEnum | undefined>(undefined);
  secureApplicationReason$ = this._secureApplicationReason$.asObservable();
  public allowJointApplications$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  deactivatePasswordPolicy: Observable<boolean>;
  codeVerification = false;
  private _deviceType!: DeviceType;
  private _brandId$: BehaviorSubject<BrandType | null> = new BehaviorSubject<BrandType | null>(null);
  private _sessionId$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  sessionId$: Observable<string | null> = this._sessionId$.asObservable();
  private _isMobile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isMobile$: Observable<boolean> = this._isMobile$.asObservable();
  private _productConfiguration$: BehaviorSubject<ProductView[]> = new BehaviorSubject<ProductView[]>([]);
  productConfiguration$ = this._productConfiguration$.asObservable();
  private _currentProductEligibility$: BehaviorSubject<EligibilityResponse | null> = new BehaviorSubject<EligibilityResponse | null>(null);
  currentProductEligibility$ = this._currentProductEligibility$.asObservable();
  private _isJointApplication$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isJointApplication$: Observable<boolean> = this._isJointApplication$.asObservable();
  private _updatedAccountSelection$: Subject<void> = new Subject();
  updatedAccountSelection$ = this._updatedAccountSelection$.asObservable();
  selectableAccountTypes$: Observable<AccountType[]> = this.accountMediator.selectableAccountTypes$;
  canHaveJointAccount$ = this.signupService.canHaveJointAccount$;

  private _updateBSAProductProperties$: Subject<void> = new Subject();
  updateBSAProductProperties$ = this._updateBSAProductProperties$.asObservable();

  // sessionId show it in Desktop view (because of width issue on small screen)
  private _isDesktopView$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isDesktopView$: Observable<boolean> = this._isDesktopView$.asObservable();

  private _fundAccountNow$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isFundingAccountRadioTouched$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _stopActionsButtonLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  stopActionsButtonLoading$: Observable<boolean> = this._stopActionsButtonLoading$.asObservable();

  private _handleEligibilityError$ = this.currentProductEligibility$.pipe(
    filter(response => !!response
      && response.status === EligibilityStatus.Failure
      && !!response?.error
      && !!response.rcCode
    )
  ).subscribe(async errorResponse => {
    await this.processOnboardingError(errorResponse!.rcCode!.toUpperCase() as ReferenceCode).toPromise();
    this.coreService.navigate(`/${ERROR_PATH}`);
  });

  private _showSuccessTracker$: Subject<boolean> = new Subject();
  showSuccessTracker$ = this._showSuccessTracker$.asObservable();

  constructor(private appSettingsService: AppSettingsService,
              private sessionStorage: SessionStorageService,
              private notificationService: NotificationService,
              private shoppingCartService: ShoppingCartService,
              private errorService: ErrorService,
              private onboardingApiService: OnboardingApiControllerService,
              private onboardingService: OnboardingService,
              private q2caliperService: Q2caliperService,
              private leiaService: ApplicationLogsControllerService,
              private leiaApplicantService: ApplicantControllerService,
              private applicationSecure: SecureApplicationControllerService,
              private skyhopperKycService: KycControllerService,
              private logger: NGXLogger,
              private readonly themeService: ThemeService,
              private skyhopperService: AddressVerificationControllerService,
              private productService: ProductControllerService,
              private dialogsService: DialogsService,
              private utils: UtilitiesService,
              private codeVerificationService: CodeVerificationService,
              private signupService: SignupService,
              private coreService: CoreService,
              private applicationLogMapper: ApplicationLogMappingService,
              private deviceDetectorService: DeviceDetectorService,
              private branchControllerService: BranchControllerService,
              private cookieService: CookieService,
              private _mediaObserver: MediaObserver,
              private dialog: MatDialog,
              private promotionService: PromotionControllerService,
              private applicationPromotionService: ApplicationPromotionControllerService,
              private accountMediator: AccountMediatorService,
              private customerControllerService: CustomerControllerService,
  ) {
    this._mediaObserver.asObservable().subscribe(mediaChange => {
      if (mediaChange[0].mqAlias === "xs") {
        this._isMobile$.next(true);
      } else {
        this._isMobile$.next(false);
      }
      // desktop view
      if (mediaChange[0].mqAlias === "md" || mediaChange[0].mqAlias === "lg" || mediaChange[0].mqAlias === "xl") {
        this._isDesktopView$.next(true);
      } else {
        this._isDesktopView$.next(false);
      }
    });

    this.deactivatePasswordPolicy = this.q2caliperService.deactivatePasswordPolicy;

    this._productConfiguration$.subscribe(value => this.accountMediator.processAccountConfiguration(value, this.productSettings));
    this._isJointApplication$.subscribe(value => this.accountMediator.accountOwnershipChanged(value));
  }

  private _product: Product | undefined = undefined;

  get product(): Product | undefined {
    return this._product;
  }

  get isBaskBrand$(): Observable<boolean> {
    return this._brandId$.pipe(
      filter(brandId => brandId === BrandType.BaskBank),
      map(() => true)
    );
  }

  get isPWABrand$(): Observable<boolean> {
    return this._brandId$.pipe(
      map(value => value === BrandType.PrivateWealth)
    );
  }

  setSecureApplicationReason(reason: SecureApplicationReasonEnum): void {
    this._secureApplicationReason$.next(reason);
  }

  setCanHaveJointAccount(isAllowed: boolean): void {
    this.signupService.canHaveJointAccount(isAllowed);
  }

  markApplicationAsJoint(): void {
    this._isJointApplication$.next(true);
  }

  updateBSAProductProperties(): void {
    this._updateBSAProductProperties$.next();
  }

  markApplicationAsIndividual(): void {
    this._isJointApplication$.next(false);
  }

  reportOLBEnrollment(status: boolean): void {
    this.notEnrolledInOLB.next(status);
  }

  /**
   * App Settings
   */
  loadAppSettings(): void {
    this.appSettingsService.LoadSettings();
  }

  getAppSettings(): Observable<Array<ConfigSetting>> {
    return this.appSettingsService.getSettings();
  }

  /**
   * Returns true if the current product or the url accessing is of Bask brand.
   */
  isBaskBrand(): boolean {
    return (+this.getBrandId() === BrandType.BaskBank) || this.isBaskURL();
  }

  isInJointMaintenanceFlow(): boolean {
    return this.isInJointMaintenanceFlow$.getValue();
  }

  isInAuthenticatedFlow(): boolean {
    return this.isInAuthenticatedFlow$.getValue()[0];
  }

  isJointApplication(): boolean {
    return this._isJointApplication$.getValue();
  }

  setJointApplicationDetails(details: string): void {
    this.addStorageItem(JOINT_APPLICATION_DETAILS_KEY, details);
    this.isInJointMaintenanceFlow$.next(true);
  }

  disableBackButton(): void {
    this.coreService.disableBackButton();
  }

   /**
   * Account Funding
   */
  markFundAccountNow(): void {
    this._fundAccountNow$.next(true);
    this._isFundingAccountRadioTouched$.next(true);
  }

  markFundAccountLater(): void {
    this._fundAccountNow$.next(false);
    this._isFundingAccountRadioTouched$.next(true);
  }

  fundAccountNow(): boolean {
    return this._fundAccountNow$.getValue();
  }

  getIsFundingAccountRadioTouched(): boolean {
    return this._isFundingAccountRadioTouched$.getValue();
  }

  markStopActionsLoading(): void {
    this._stopActionsButtonLoading$.next(true);
  }

  /**
   * Falcon Service
   */
  async getGlobalSettings(version: number): Promise<Array<FalconSetting>> {
    this.globalSettings$ = this.onboardingApiService.getGlobalSettings1(version);

    return new Promise((resolve, reject) => {
      this.globalSettings$.pipe(
        map((items) => items.filter(setting => setting.enabled)
            .map(item => (new FalconSetting(item.id as number, item.name as string,
              "", item.value as string, item.description as string,
              item.language as string, item.version as number
            ))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems) => {
          resolve(mappedItems);
        }, error => {
          let log = this.utils.createLog("BB8", error.toString());
          this.handleInternalError(null, StatusEnum.Rejected, [log]);
        });
    });
  }

  async getBrandById(brandId: number): Promise<Brand> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getBrand(brandId);

      results.subscribe((data) => {
        let brand = new Brand(data.id, data.name);

        data.products?.forEach(curProd => {
          let product = new Product(curProd.id as number, curProd.name as string, curProd.code as number, curProd.enabled as boolean)
          brand.products.push(product);
        });

        resolve(brand)
        reject("ERROR");
      }, error => {
        let log = this.utils.createLog("BB8", error.toString());
        this.handleInternalError(null, StatusEnum.Rejected, [log]);
      });
    })
  }

  async getBrandByName(brandName: string): Promise<Brand> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getBrand1(brandName);

      results.subscribe((data) => {
        let brand = new Brand(data.id, data.name);

        data.products?.forEach(curProd => {
          let product = new Product(curProd.id as number, curProd.name as string, curProd.code as number, curProd.enabled as boolean)
          brand.products.push(product);
        });

        if (environment.development) {
          this.logger.debug("Settings for Brand=" + brandName + " " + JSON.stringify(brand));
        }

        resolve(brand)
        reject("ERROR");
      }, error => {
        let log = this.utils.createLog("BB8", error.toString());
        this.handleInternalError(null, StatusEnum.Rejected, [log]);
      });
    })
  }

  async getBrandSettings(version: number, brandId: number, productUuid?: string): Promise<Array<FalconSetting>> {
    return new Promise((resolve, reject) => {
      if (productUuid != undefined) {
        var results = this.onboardingApiService.getSettingsForProduct1(productUuid, version);
      } else {
        var results = this.onboardingApiService.getSettingsForBrand1(brandId, version);
      }
      results.pipe(
        map((items: any) => items.filter((setting: any) => setting.enabled)
            .map((item: any) => (new FalconSetting(item.id as number, item.name as string,
              "", item.value as string, item.description as string,
              item.language as string, item.version as number
            ))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems: any) => {
          resolve(mappedItems);
        }, (error: any) => {
          let log = this.utils.createLog("BB8", error.toString());
          this.handleInternalError(null, StatusEnum.Rejected, [log]);
        });
    });
  }

  isActiveProduct(productUuid: string): Observable<boolean> {
    return this.onboardingApiService.getProductDetailsByUniqueId(productUuid).pipe(
      map((data: any) => {
        let product = new Product(data.id, data.name, data.code, data.enabled, data.brand?.id, data.brand?.name, data.descriptors, data.infoUrl);

        const isActive = product && product.code > 0;

        this.coreService.setCanAccess(isActive);

        return isActive;
      }),
      catchError(err => {
        this.logger.error(err);
        return of(false);
      })
    );
  }

  async getProduct(productUuid: string): Promise<Product> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getProductDetailsByUniqueId(productUuid, 'body');
      results.subscribe((data: any) => {
        const product = new Product(data.id, data.name, data.code, data.enabled, data.brand?.id, data.brand?.name, data.descriptors, data.infoUrl, "|", data.accountType, data.productType, data.interestRate, data.interestPlanCode, data.olbGroupId, data.alloyWorkflowId, data.allowJointApplications, data.productUID, data.allowMultiProductApplications);
        this._product = product;

        resolve(product);
      }, (error: any) => {
        let log = this.utils.createLog("BB8", "Product detail not loaded.");
        reject(log);
      });
    });
  }

  async getProductById(productUuid: string): Promise<Product> {
    return new Promise((resolve, reject) => {
      let results = this.onboardingApiService.getProductDetailsByUniqueId(productUuid);

      results.subscribe((data: any) => {
        resolve(new Product(data.id, data.name, data.code, data.enabled, data.brand?.id, data.brand?.name, data.descriptors, data.infoUrl, "|", data.accountType, data.productType, data.interestRate, data.interestPlanCode, data.olbGroupId, data.alloyWorkflowId, data.allowJointApplications, data.productUID, data.allowMultiProductApplications));
      }, (error: any) => {
        let log = this.utils.createLog("BB8", error.toString());
        this.handleInternalError(null, StatusEnum.Rejected, [log]);
      });
    });
  }

  setBrandId(brandId: string): void {
    this.addStorageItem("brandId", brandId);
    this._brandId$.next(Number(brandId));
  }

  setSessionId(sessionId: string): void {
    this.addStorageItem(LOCAL_SESSION_ID, sessionId);
    this._sessionId$.next(sessionId);
    datadogRum.setUserProperty('id', sessionId);
  }

  getBrandId(): string {
    return this.getStorageItem("brandId");
  }

  /**
   * Notification Service
   */
  showSuccess(message: string | "", title: string | "") {
    this.notificationService.showSuccess(message, title)
  }

  showError(message: string | "", title: string | "") {
    this.notificationService.showError(message, title)
  }

  showErrorPage(message: string | "", title: string | "") {
    this.errorService.showErrorPage(message, title);
  }

  showInfo(message: string | "", title: string | "") {
    this.notificationService.showInfo(message, title)
  }

  showWarning(message: string | "", title: string | "") {
    this.notificationService.showWarning(message, title)
  }

  /***
   * add key/value pair to session storage
   * @param key
   * @param value
   */
  addStorageItem(key: string, value: string): void {
    this.sessionStorage.set(key, value);
  }

  /***
   * clear the session storage
   */
  clearStorage(): void {
    this.sessionStorage.clear();
    this.globalSettingsLoaded.next(false);
  }

  getConfigurationSetting(key: Configuration): string {
    return this.getStorageItem(key);
  }

  /***
   * get the storage item by key
   * @param key
   */
  getStorageItem(key: string): string {
    return this.sessionStorage.get(key);
  }

  /***
   * get the storage item by key and parse to boolean value
   * @param key
   */
  getStorageItemAsBoolean(key: string): boolean {
    return JSON.parse(this.sessionStorage.get(key));
  }

  /**
   * Watch a Session Storage item to receive updates on settings.
   * @param key setting name to watch.
   */
  watchStorageItem(key: string): Observable<any> | undefined {
    return this.sessionStorage.watch(key);
  }

  /***
   * add key/value pair to local storage
   * @param key
   * @param value
   */
  addLocalStorageItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  /***
   * clear the local storage
   */
  clearLocalStorage(): void {
    localStorage.clear();
  }

  /***
   * get the local storage item by key
   * @param key
   */
  getLocalStorageItem(key: string): string {
    let item = localStorage.getItem(key);
    if (item === "undefined" || item == null) {
      item = null;
    } else {
      item = JSON.parse(item);
    }
    return item!;
  }

  /**
   * Shopping Cart
   */
  getShoppingCartOnboardingRequest(): OnboardingRequest {
    if (environment.development) {
      this.showInfo(JSON.stringify(this.shoppingCartService.onboardingRequest), "");
    }
    // can be null or already set
    // returning a new reference to avoid secondary effects
    return this.utils.cloneObject(this.shoppingCartService.onboardingRequest);
  }

  addCartItem(product: Product): void {
    this.shoppingCartService.addCartItem(product);
  }

  setShoppingCartOnboardingRequest(onboardingRequest: OnboardingRequest): void {
    this.shoppingCartService.onboardingRequest = onboardingRequest;
    if (environment.development) {
      this.showInfo(JSON.stringify(this.shoppingCartService.onboardingRequest), "");
    }
  }

  pushShoppingCartOnboardingRequest(onboardingRequest: OnboardingRequest): void {
    this.shoppingCartOnboardingRequest$.next(onboardingRequest);
  }

  getShoppingCartCustomerInfo(): Customer {
    return this.shoppingCartService.customer;
  }

  setShoppingCartSetCustomerInfo(customer: Customer): void {
    this.shoppingCartService.customer = customer;
  }

  /**
   * Get the Application Logs Update Request object for use with Leia service
   */
  getShoppingCartApplicationLogsRequest(): ApplicationLogsRequest {
    return this.shoppingCartService.applicationLogsRequest;
  }

  /**
   * Set the Application Logs Update Request object for use with Leia service
   */
  setShoppingCartApplicationLogsRequest(applicationLogsUpdateRequest: ApplicationLogsRequest) {
    return this.shoppingCartService.applicationLogsRequest = applicationLogsUpdateRequest;
  }

  /**
   * Leia Service
   */

  /**
   * Q2 Caliper service
   */
  async getPasswordPolicy(brandName: string): Promise<PasswordPolicy> {
    return this.q2caliperService.getPasswordPolicy(brandName);
  }

  /**
   * Create a new session in the leia service and set the session ID.
   */
  async createNewSession(route: ActivatedRouteSnapshot, callback: () => void) {
    const applicationLogRequest = this.getShoppingCartApplicationLogsRequest();
    //Set the channel info
    applicationLogRequest.applicationChannelId = ApplicationChannel.Digital;
    applicationLogRequest.isJoint = 0;
    applicationLogRequest.applicationType = ApplicationType.NewClientApplication;
    applicationLogRequest.url = location.href;

    if (this.isFromBranch()) {
      applicationLogRequest.applicationChannelId = ApplicationChannel.Branch;
      applicationLogRequest.branchId = this.getLocalStorageItem(BRANCH_ID);
    }

    this.handleUserAgentProperties(applicationLogRequest, "Application Started. Browser Details: " + JSON.stringify(this.deviceDetectorService.getDeviceInfo()));

    const utm = route.queryParamMap.get("utm");
    if (utm) {
      let step = this.utils.createStep(Step.NameEnum.ProductSelection, Step.StateEnum.Success, []);
      let applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.ProductionSelection, OnboardingStateEnum.Success, OnboardingSourceEnum.Bb8, []);
      applicationLogStepRequest.applicationLogDetailsRequestList = new Array();

      applicationLogRequest.utmCampaign = utm;
      step.logs!.push(this.utils.createLog("BB8", `UTM parameter detected. Value = ${utm}`));
      applicationLogStepRequest.applicationLogDetailsRequestList.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, `UTM parameter detected. Value = ${utm}`));

      applicationLogRequest.step = step;
      applicationLogRequest.applicationLogStepRequest = applicationLogStepRequest;
    }

    this.leiaService.startSession(applicationLogRequest).subscribe((response: any) => {
      let sessionId = response.toString();
      applicationLogRequest.sessionId = sessionId;
      this.setSessionId(sessionId);
      this.setShoppingCartApplicationLogsRequest(applicationLogRequest);

      if (environment.development) {
        this.logger.log("Session created with ID " + sessionId);
      }
      callback();
    }, (error: any) => {
      let log = this.utils.createLog("BB8", "Unable to start session.");
      let log2 = this.utils.createLog("BB8", error.toString());
      this.handleInternalError(null, StatusEnum.Rejected, [log, log2]);
    });
  }

  /**
   * Method to extract the user agent, device type, and browser details and persist them.
   *
   * @param applicationLogRequest The current application object. Provided to this method to ensure we are capturing
   * any potential updates to properties of this object before persistence.
   * @param details The details to persist to the log entry.
   */
  public handleUserAgentProperties(applicationLogRequest: ApplicationLogsRequest, details: string) {
    let log = this.utils.createLog(OnboardingSourceEnum.Bb8, details);
    let step = this.utils.createEmptyStepForLogging([log]);

    let applicationLogDetailRequest = this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, details);

    applicationLogRequest.step = step;

    applicationLogRequest.applicationLogDetailsRequestList = new Array<ApplicationLogDetailsRequest>();
    applicationLogRequest.applicationLogDetailsRequestList?.push(applicationLogDetailRequest);

    const browser = this.deviceDetectorService.browser;
    const device = this.deviceDetectorService.deviceType;

    applicationLogRequest.browser = browser;
    applicationLogRequest.device = device;
  }

  attachExistingSession(sessionId: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const applicationLogRequest = this.getShoppingCartApplicationLogsRequest();
      applicationLogRequest.sessionId = sessionId;

      this.setShoppingCartApplicationLogsRequest(applicationLogRequest);
      this.setSessionId(sessionId);
      resolve();
    });
  }

  executeAlloyCall(evaluateRequest: EvaluateRequest, documentVerify: boolean, alloyWorkFlowId: string, entityTokenId: string) {
    if (!documentVerify) return this.skyhopperKycService.evaluate(evaluateRequest, alloyWorkFlowId);
    else return this.skyhopperKycService.documentVerification(entityTokenId, evaluateRequest, alloyWorkFlowId);
  }

  createNewApplicant(): Observable<number> {
    return this.leiaApplicantService.startApplicantSession(this.getShoppingCartApplicationLogsRequest());
  }

  createNewApplicantIdentityResponse(applicantRequest: ApplicantRequest): Observable<number> {
    return this.leiaApplicantService.startApplicantIdentityResponseSession(applicantRequest);
  }

  addApplicant(applicant: OnboardingApplicant): void {
    const onboardingRequest = this.getShoppingCartOnboardingRequest();
    onboardingRequest.applicants.push(applicant);
    this.setShoppingCartOnboardingRequest(onboardingRequest);
  }

  deleteApplicant(uuId: string | undefined, applicantId: number): Promise<number> {
    if (!uuId) {
      return new Promise((resolve, reject) => {
        reject();
      });
    }

    return this.leiaApplicantService.deleteApplicant(uuId, applicantId).toPromise();
  }

  async handleInternalError(stepName: NameEnum | null, appState: StatusEnum, logs: Log[]) {
    let applicationRequest = this.getShoppingCartApplicationLogsRequest();

    applicationRequest.step = stepName !== null ? this.utils.createStep(stepName, StateEnum.Fail, logs) : this.utils.createEmptyStepForLogging(logs);
    applicationRequest.status = appState;

    this.setShoppingCartApplicationLogsRequest(applicationRequest);
    await this.updateApplication();

    this.processOnboardingError(ReferenceCode.InternalFailure).subscribe(() => {
      this.coreService.navigate(`/${ERROR_PATH}`);
    });
  }

  /**
   * Update application logs request to the leia service.
   */
  updateApplication(): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      const applicationLogRequest = this.getShoppingCartApplicationLogsRequest();

      UtilitiesService.sanitizeApplicantsDateOfBirth(applicationLogRequest);

      if (!!this.getStorageItem("SessionId"))
        applicationLogRequest.sessionId = this.getStorageItem("SessionId");

      this.setShoppingCartApplicationLogsRequest(applicationLogRequest);

      if (environment.development)
        this.logger.log("updateApplication:" + JSON.stringify(applicationLogRequest));

      if (applicationLogRequest.sessionId) {
        try {
          await this.leiaService.update(
            this.applicationLogMapper.mapEmploymentDetailsIdFieldsToDescriptions(
              this.getShoppingCartApplicationLogsRequest(),
              this.getCountries(),
              this.getAnnualIncomes(),
              this.getAllOccupations(),
              this.getAllIncomeSources()
            )
          ).toPromise();

          // Reset / Remove the new Step / LogDetails
          applicationLogRequest.applicationLogStepRequest = undefined;
          applicationLogRequest.applicationLogDetailsRequestList = new Array<ApplicationLogDetailsRequest>();
          this.setShoppingCartApplicationLogsRequest(applicationLogRequest);

          resolve();
        } catch (error) {
          reject(error);
          this.errorService.showErrorPage((error as any).error.detail, ReferenceCode.InternalFailure);
          if (environment.development) {
            this.logger.log("Failed to update application log to leia service - " + error);
          }
        }
      }
    });
  }

  /**
   * USPS Address Verify Service request to the skyhopper service.
   */
  addressVerify(addressVerifyRequest: AddressVerificationRequest): Observable<AddressVerificationResponse> {
    return this.skyhopperService.verifyAddress(addressVerifyRequest);
  }

  /**
   * Set the theme file for the site based on brand name
   * @param brandName Brand name to load theme file for.
   */
  setBrandTheme() {
    let themeFile = this.getStorageItem("theme-file");
    if (themeFile == null) {
      // pull default theme file if brand theme file is not set.
      themeFile = this.getStorageItem("default-theme-file");
    }
    this.themeService.set(themeFile);
  }

  createApplicationCompleteDialog(): MatDialogRef<any> {
    return this.dialogsService.createDialog(ApplicationDialog.COMPLETE)!;
  }

  createApplicationErrorDialog(): MatDialogRef<any> {
    return this.dialogsService.createDialog(ApplicationDialog.ERROR)!;
  }

  createJointMaintenanceInvitation(jointApplicantRequest: JointApplicantRequest): Observable<string> {
    return this.leiaApplicantService.createJointApplicant(jointApplicantRequest);
  }

  processOnboarding(accountType: string, accountNumber: string, firstName: string, userName: string, isExistingCustomer: boolean): Observable<any> {
    return this.onboardingService.processOnboarding(accountType, accountNumber, firstName, userName, isExistingCustomer);
  }

  processOnboardingError(code: ReferenceCode): Observable<any> {
    this.logExceptionCode(code);
    return this.onboardingService.processOnboardingError(code);
  }

  processOnboardingUnderReview(code: ReferenceCode): Observable<any> {
    this.logExceptionCode(code);
    return this.onboardingService.processOnboardingUnderReview(code);
  }

  getNewAccountInformation(): OnboardingSuccessfulResponse {
    return this.onboardingService.getNewAccountInformation();
  }

  getAccountCreationError(): OnboardingExceptionResponse {
    return this.onboardingService.getAccountCreationError();
  }

  getSuccessConfiguration(): SuccessConfiguration {
    let newLoginUrl:string='';
    const loginUrlQuantumMetricSessionID = this.cookieService.get('QuantumMetricSessionID') ? `?qms=${this.cookieService.get('QuantumMetricSessionID')}`:'';
    const quantumMetricUserID = this.cookieService.get('QuantumMetricUserID') ? `&qmuser=${this.cookieService.get('QuantumMetricUserID')}`:'';
    const loginUrl = this.getStorageItem(SUCCESS_LOGIN_URL_KEY);
    const loginUrlArray = loginUrl.split("#");
    if(loginUrlArray.length > 1 && quantumMetricUserID && loginUrlQuantumMetricSessionID) {
      newLoginUrl = `${loginUrlArray[0]}${loginUrlQuantumMetricSessionID}${quantumMetricUserID}#${loginUrlArray[1]}`;
    } else {
      newLoginUrl = loginUrl;
    }

    return {
      cancelUrl: this.getStorageItem(SUCCESS_CANCEL_URL_KEY),
      customerSupportPhone: this.getStorageItem(CUSTOMER_SUPPORT_PHONE_KEY),
      howToContact: this.getStorageItem(SUCCESS_HOW_TO_CONTACT_KEY),
      loginUrl: `${newLoginUrl}`,
      nextSteps: this.isInAuthenticatedFlow() ? this.getStorageItem(SettingsEnum.AuthenticatedFlowNextSteps) : this.getStorageItem(SUCCESS_NEXT_STEPS_KEY),
      newAccountMessage: this.getStorageItem(SUCCESS_MESSAGE_KEY),
      newJointAccountMessage: this.getStorageItem(SUCCESS_JOINT_MESSAGE_KEY),
      title: this.getStorageItem(SUCCESS_TITLE_KEY)
    }
  }

  getExceptionConfiguration(): ReferenceConfiguration {
    const customerSupportEmail = this.getStorageItem(CUSTOMER_SUPPORT_EMAIL_KEY);
    const customerSupportPhone = this.getStorageItem(CUSTOMER_SUPPORT_PHONE_KEY);
    const title = this.getStorageItem(ERROR_TITLE_KEY);

    if (this.errorService.currentErrorCode) {
      return {
        customerSupportEmail,
        customerSupportPhone,
        refCode: this.errorService.currentErrorCode,
        title,
        props: {
          codes: [],
          message: this.errorService.currentError,
          contactEmail: '',
          contactMessage: ''
        }
      }
    }

    const {refCode} = this.getAccountCreationError();
    const error_prop_key = this.findTheErrorPropKey(refCode);
    return {
      customerSupportEmail,
      customerSupportPhone,
      refCode,
      title,
      props: this.getReferencePropsObject(error_prop_key)
    };
  }

  findTheErrorPropKey(refCode: string): string {
    switch (refCode) {
      case ReferenceCode.AlloyDenied:
      case ReferenceCode.DocVDenied:
        return RC_ERROR_PROPS_KEY;
      case ReferenceCode.StatusUFailure:
        return RC_STATUS_U_ERROR_PROPS_KEY;
      case ReferenceCode.ProductCheckFailure:
        return RC_PRODUCT_ELIGIBILITY_ERROR_KEY;
      case ReferenceCode.AccountRulesFailure:
        return RC_ACCOUNT_RULES_ERROR_KEY;
      case ReferenceCode.JointAccountEnrollmentFailure:
        return RC_JOINT_ACCOUNT_ERROR_KEY;
      case ReferenceCode.HYSAAccountRulesFailure:
        return RC_HYSA_ACCOUNT_RULES_ERROR_KEY;
      case ReferenceCode.ApplicantInfoMismatch:
        return RC_APPLICANT_INFO_MISMATCH_ERROR_KEY;
      case ReferenceCode.AlloyQualifileDecline:
        return RC_ERROR_PROPS_KEY;
      default:
        return RC_UNDER_REVIEW_PROPS_KEY;
    }
  }

  getReferencePropsObject(key: string): ReferenceProperties {
    let propsString = this.getStorageItem(key);

    return JSON.parse(propsString);
  }

  verifyCode(request: SecureApplicationVerificationRequest): Observable<SecureApplicationVerificationResponse> {
    return this.codeVerificationService.verify(request);
  }

  checkValidity(secureSessionId: string): Observable<SecureApplicationValidityResponse> {
    return this.codeVerificationService.checkValidity(secureSessionId);
  }

  getSessionIdFromSecureSessionId(secureSessionId: string): Observable<string> {
    this.codeVerification = true;
    return this.codeVerificationService.getSessionId(secureSessionId)
  }

  getExpiryDateFromSessionId(sessionId: string): Observable<string> {
    return this.applicationSecure.getExpiryDateFromSessionId(sessionId);
  }

  /**
   * Gets the secure application details from the database.
   * @param secureSessionId
   *
   */
  getJointAccountSecureDetails(secureSessionId: string): Observable<JointAccountSecureApplicationResponse> {
    return this.applicationSecure.getJointAccountApplicationSecureDetails(secureSessionId);
  }

  /**
   * Gets the secure application details from the database.
   * @param secureSessionId
   *
   */
  getSecureApplicationMetadata(secureSessionId: string): Observable<SecureApplicationMetadata> {
    return this.applicationSecure.getApplicationSecureMetadata(secureSessionId);
  }

  getApplicationLog(sessionId: string): Observable<ApplicationDetailLog> {
    return this.codeVerificationService.getSessionData(sessionId);
  }

  getClientIPAddress(): string {
    return this.getShoppingCartApplicationLogsRequest().clientIpAddress!;
  }

  getSessionId(): string {
    return this.getStorageItem("SessionId")
  }

  getCountries(): Array<Country> {
    return this.signupService.getCountries();
  }

  loadCountries(): BehaviorSubject<Array<Country>> {
    return this.signupService.loadCountries();
  }

  loadAnnualIncomes(): BehaviorSubject<Array<AnnualIncome>> {
    return this.signupService.loadAnnualIncomes();
  }

  getAnnualIncomes(): Array<AnnualIncome> {
    return this.signupService.getAnnualIncomes();
  }

  loadAllIncomeSources(): BehaviorSubject<Array<IncomeSource>> {
    return this.signupService.loadAllIncomeSources();
  }

  getAllIncomeSources(): Array<IncomeSource> {
    return this.signupService.getAllIncomeSources();
  }

  loadAllOccupations(): BehaviorSubject<Array<Occupation>> {
    return this.signupService.loadAllOccupations();
  }

  getAllOccupations(): Array<Occupation> {
    return this.signupService.getAllOccupations();
  }

  loadAllStates(): BehaviorSubject<Array<State>> {
    return this.signupService.loadAllStates();
  }

  getAllStates(): Array<State> {
    return this.signupService.getAllStates();
  }

  getProductTypes(): Observable<ProductTypeDetails[]> {
    return this.productService.getProductTypes();
  }

  shouldHideBsaQuestions(): boolean {
    const { products } = this.getShoppingCartApplicationLogsRequest();
    let shouldHideBSAQuestions = !(!!products?.find(product => product.productType === ProductType.Checking));

    // US447622 Changes - Additional Check for ICS product, Suppress account details.
    // Set true if the product config setting value is true
    if (!!this.getStorageItem(SUPPRESS_ACCOUNT_DETAILS) &&
      this.getStorageItem(SUPPRESS_ACCOUNT_DETAILS).toLowerCase() === SUPPRESS_ACCOUNT_DETAILS_VALUE) {
      shouldHideBSAQuestions = true;
    }

    return shouldHideBSAQuestions;
  }

  formElementHasChanged(): boolean {
    return this.signupService.formElementHasChanged;
  }

  async cleanupApplication(abandonReason: number) {
    const applicationRequest = this.getShoppingCartApplicationLogsRequest();
    if (applicationRequest.status !== StatusEnum.Approved && applicationRequest.status !== StatusEnum.Rejected) {
      await this.abandonApplicationIfNeeded(abandonReason);
    }
  }

  getSecuraApplicationReason(): SecureApplicationReasonEnum | undefined {
    return this._secureApplicationReason$.getValue();
  }

  updateApplicationInOLBCredentialsPage() : Promise<void> {
    const applicationLogDetailsRequest = this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, "Create OLB Credentials has failed.");
    const additionalLogDetailsStep = this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, `Encountered ${ReferenceCode.OnlineBankingEnrollmentFailure.toString()}`);

    const applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.CaliperUserEnrollment, OnboardingStateEnum.Fail, OnboardingSourceEnum.Bb8, [applicationLogDetailsRequest, additionalLogDetailsStep]);

    let applicationRequest = this.getShoppingCartApplicationLogsRequest();

    applicationRequest.rcCode = ReferenceCode.OnlineBankingEnrollmentFailure;
    applicationLogStepRequest.rcCode = ReferenceCode.OnlineBankingEnrollmentFailure;

    applicationRequest.applicationLogStepRequest = applicationLogStepRequest;
    applicationRequest.status = StatusEnum.Approved;

    this.setShoppingCartApplicationLogsRequest(applicationRequest);
    return this.updateApplication();
  }

  async abandonApplicationIfNeeded(abandonReason: number) {
    let application = this.getShoppingCartApplicationLogsRequest();

    // Check if application might have been finished through an external process (Coruscant)
    const applicationDetailLog = await this.getApplicationLog(application.sessionId ?? '').toPromise();
    if (this._inFinishedState(applicationDetailLog.status as StatusEnum)) {
      return;
    }

    if (!this._isApplicationInFinishedState()) {
      application.status = StatusEnum.Abandoned;

      let logDetail = "Application is abandoned.";
      if (abandonReason != null) {
        if (abandonReason === AbandonReason.Idle)
          logDetail = "Session idle timeout. Application is abandoned.";
        else if (abandonReason === AbandonReason.MaxTime)
          logDetail = "Maximum application start to end time expired. Application is abandoned.";
        else if (abandonReason === AbandonReason.Unload)
          logDetail = "User left page or tab closed. Application is abandoned.";
      }
      const log = this.utils.createLog("BB8", logDetail);
      application.step = this.utils.createEmptyStepForLogging([log]);

      application.applicationLogDetailsRequestList?.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, logDetail));

      this.setShoppingCartApplicationLogsRequest(application);
      await this.updateApplication();
    }
  }

  _isApplicationInFinishedState(): boolean {
    let application = this.getShoppingCartApplicationLogsRequest();

    return this._inFinishedState(application.status!);
  }

  private _inFinishedState(status: StatusEnum): boolean {
    const finishedStates = [
      StatusEnum.Approved,
      StatusEnum.Rejected,
      StatusEnum.UnderReview,
      StatusEnum.Canceled,
      StatusEnum.Error
    ];

    return finishedStates.includes(status);
  }

  getInterestPlanCodes(product: Product): number {
    let interestCode: number;
    //takes first element from the integer array.
    if (product.interestPlanCodes!.length > 0) {
      interestCode = product.interestPlanCodes.shift()!;
    }
    return interestCode!;
  }

  getCatalogData(): Observable<any> {
    return combineLatest([
      this.loadAllIncomeSources(),
      this.loadAllOccupations(),
      this.loadAllStates(),
      this.loadAnnualIncomes(),
      this.loadCountries()
    ]).pipe(
      filter((results: any[]) =>
        !!(results[0] as IncomeSource[]).length &&
        !!(results[1] as Occupation[]).length &&
        !!(results[2] as State[]).length &&
        !!(results[3] as AnnualIncome[]).length &&
        !!(results[4] as Country[]).length
      ),
      take(1)
    );
  }

  isFromBranch(): boolean {
    return !!this.getLocalStorageItem(BRANCH_ID);
  }

  setDeviceType(): void {
    if (this.deviceDetectorService.isTablet()) {
      this._deviceType = DeviceType.TABLET;
      return;
    }
    if (this.deviceDetectorService.isMobile()) {
      this._deviceType = DeviceType.MOBILE;
      return;
    }

    this._deviceType = DeviceType.DEFAULT;
  }

  getDeviceType(): DeviceType {
    return this._deviceType;
  }

  getBranchList(): Observable<any> {
    return this.branchControllerService.getBranches();
  }

  getIsMinor(): boolean {
    return !!this.getShoppingCartApplicationLogsRequest().products?.filter(product => product.productDescription?.trim() === MINOR_SAVINGS_PRODUCT_NAME).length;
  }

  getIsEmployeeProduct(): boolean {
    return !!this.getShoppingCartApplicationLogsRequest().products?.filter(product => product.productCode?.trim() === EMPLOYEE_CHECKING_PRODUCT_CODE).length;
  }

  getIsEmployeeSavingsProduct(): boolean {
    return !!this.getShoppingCartApplicationLogsRequest().products?.filter(product => product.productCode?.trim() === EMPLOYEE_SAVINGS_PRODUCT_CODE).length;
  }

  clearAllCookies() {
    this.cookieService.deleteAll("/");
    //All possible formats for authCookie below
    this.cookieService.set("authCookie", '', new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/");
    this.cookieService.set("authCookie", '', new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/assets");
    this.cookieService.set("authCookie", '', new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/assets/theme");
    this.cookieService.set("authCookie", '', new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/assets/images/tcb");
    this.cookieService.set("authCookie", '', new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/assets/images/bask");
  }

  verifyPromocode(promotionVerificationRequest: PromotionVerificationRequest): Observable<PromotionVerificationResponse> {
    return this.promotionService.verifyPromotionCode(promotionVerificationRequest);
  }

  /**
   * Get the Application applied promotions
   */
  getAppliedPromotions(): Array<Promotion> {
    return this.shoppingCartService.applicationPromotions;
  }

  /**
   * Set the applied promotions to session
   */
  setAppliedPromotions(value: Array<Promotion>) {
    return this.shoppingCartService.applicationPromotions = value;
  }

  deletePromoCode(selectedPromocode: string): Promise<any> {
    let applicationPromotionDetails = new ApplicationPromotionDetails();
    applicationPromotionDetails.promotionCode = selectedPromocode;
    applicationPromotionDetails.sessionId = this.getSessionId();
    return this.applicationPromotionService.deletePromotion(applicationPromotionDetails).toPromise();
  }

  private executeAlloyEvaluate(evaluateRequest: EvaluateRequest, alloyWorkFlowId: string): Observable<SalesForceAlloyObject> {
    return this.skyhopperKycService.evaluate(evaluateRequest, alloyWorkFlowId);
  }

  private executeAlloyDocumentVerification(evaluateRequest: EvaluateRequest, alloyWorkFlowId: string, entityTokenId: string): Observable<SalesForceAlloyObject> {
    return this.skyhopperKycService.documentVerification(entityTokenId, evaluateRequest, alloyWorkFlowId);
  }

  private async logExceptionCode(code: ReferenceCode) {
    let appRequest = this.getShoppingCartApplicationLogsRequest();

    appRequest.step!.logs = [
      this.utils.createLog("BB8", `Encountered ${code.toString()}`)
    ];

    appRequest.applicationLogDetailsRequestList = [this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, `Encountered ${code.toString()}`)];

    appRequest.rcCode = code.toString();

    if (code === ReferenceCode.InternalFailure ||
      code === ReferenceCode.EligibilityReviewFailure ||
      code === ReferenceCode.AlloyFailure ||
      code === ReferenceCode.SocureFailure ||
      code === ReferenceCode.GeneralFailure ||
      code === ReferenceCode.DocVFailure) {
      appRequest.status = StatusEnum.Error;
    }

    this.setShoppingCartApplicationLogsRequest(appRequest);
    await this.updateApplication();
  }

  /**
   * Returns true the current url that is being accessed is of Bask brand.
   */
  isBaskURL(): boolean {
    return window.location.href.toString().toLowerCase().indexOf(BASK_URL_CHECK) !== -1;
  }

  async updateApplicationOnSubmit(sessionId: string): Promise<void> {
    await this.leiaService.updateApplicationOnSubmit(sessionId).toPromise();

    let applicationRequest = this.getShoppingCartApplicationLogsRequest();
    let step = this.utils.createStep(NameEnum.TermsAndDisclosures, "Success", [this.utils.createLog("BB8", "User accepted terms and conditions.")]);
    let applicationLogStepRequest = this.utils.createApplicationLogStepRequest(OnboardingStepEnum.TermsAndDisclosures, OnboardingStateEnum.Success, OnboardingSourceEnum.Bb8, [this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, "User accepted terms and conditions.")]);

    applicationRequest.step = step;
    applicationRequest.applicationLogStepRequest = applicationLogStepRequest;

    this.setShoppingCartApplicationLogsRequest(applicationRequest);
    await this.updateApplication();
  }

  showSuccessTracker(): void {
    this._showSuccessTracker$.next(true);
  }

  async getProductConfigurationByBrand(product: Product): Promise<ProductView[]> {
    const configuration = await this.onboardingApiService.getProductsByBrands(
      product.brandId.toString(),
      this.isInJointMaintenanceFlow() || !product.allowMultiProductApplications
    ).toPromise();

    const isMultiProductAllowed = SignupService.isMultiProductIsAllowed(
      this.getStorageItem("productUid"),
      configuration,
      this._secureApplicationReason$.getValue()
    );

    this.signupService.setIfMultiProductAllowed(isMultiProductAllowed);

    this.setProductConfiguration(configuration);

    return configuration;
  }

  private _addProductToConfiguration(product: Product, configuration: ProductView[]): void {
    if (!configuration.length) {
      return;
    }

    const { productUID, name, code, interestRate } = product;

    configuration[0].products!.push({
      productUID,
      name,
      code,
      apy: interestRate
    })
  }

  setProductConfiguration(configuration: ProductView[]) {
    this._productConfiguration$.next(configuration);
  }

  getAccountConfiguration(accountValue: string): ProductDetails {
    let account: ProductDetails;

    for (let i = 0; i < this._productConfiguration$.value.length; i++) {
      const type = this._productConfiguration$.value[i];
      const foundAccount = type.products!.find(value => value.productUID === accountValue);

      if (foundAccount) {
        account = foundAccount;
        break;
      }
    }

    return account!;
  }

  async getProductListConfiguration(productUIDList: string): Promise<Array<ProductDetails>> {
    const productDetails = await this.onboardingApiService.getProductsByListOfCode(productUIDList).toPromise();

    return productDetails;
  }

  initializeAccountSelectionValidation(): void {
    this.signupService.accountSelection.clearAsyncValidators();
    this.signupService.accountSelection.addAsyncValidators(ProductEligibilityValidator.create(this));
    this.signupService.accountSelection.updateValueAndValidity({emitEvent: true});
  }

  async getCurrentProductSelectionEligibility(): Promise<EligibilityResponse> {
    const productEligibility = await this.customerControllerService.getProductEligibility(this.getSessionId()).toPromise();

    /*
    const productEligibility = {
      "status": "Success",
      "error": undefined,
      "applicants": [],
      "products": [
          {
              "productCode": "113",
              "applicationProductUID": "f6de42f8-e733-4c90-9d71-28826623b5e2",
              "isEligible": false,
              "message": "The Star High-Yield savings account is only open to residents of Texas.|You've reached the maximum number allowed for this type of savings account."
          }
      ],
      "rcCode": undefined
    };
    */
    this._currentProductEligibility$.next(productEligibility);

    this.initializeAccountSelectionValidation();

    return productEligibility;
  }

  async addProductSelectionToApplication(status: StatusEnum = StatusEnum.Started): Promise<void> {
    const applicationRequest = this.getShoppingCartApplicationLogsRequest();

    const selectedAccounts = (this.signupService.accountSelection.value.accounts as Array<SelectedAccount>);
    const selectedAccountsUIDList = selectedAccounts.map(account => account.account).join(",");

    const productDetails = await this.getProductListConfiguration(selectedAccountsUIDList);
    let currentProductList: ApplicationProductRequest[] = [];

    if (applicationRequest.products && applicationRequest.products.length) {
      currentProductList = JSON.parse(JSON.stringify(applicationRequest.products));
    }

    applicationRequest.status = status;

    applicationRequest.productUIDList = selectedAccountsUIDList;
    applicationRequest.products = selectedAccounts.map(account => {
      const currentProductRecord = currentProductList.find(record =>
        record.applicationProductUID === account.applicationProductUID &&
        record.productCode === account.accountCode
      );

      let applicationProductUID = undefined;
      if (currentProductRecord) { applicationProductUID = currentProductRecord.applicationProductUID; }
      const {
        accountType, code, name, productType, productUID
      } = productDetails.find(productDetail => productDetail.productUID === account.account)!;
      const {
        orderChecks, orderDebitCard, orderDebitCardForPrimary, orderDebitCardForCoApplicant,
      } = account;

      const isJoint = this._isJointApplication$.getValue();
      const orderChecksApplicantType = this._getOrderApplicantType(
        isJoint, orderChecks
      );
      const orderDebitApplicantType = this._getOrderApplicantType(
        isJoint, orderDebitCard, orderDebitCardForPrimary, orderDebitCardForCoApplicant
      );

      return {
        accountType,
        applicationProductUID,
        productCode: code?.toString(),
        productDescription: name,
        productType,
        productUID,
        orderDebitCard,
        orderChecks,
        orderChecksApplicantType,
        orderDebitApplicantType
      }
    });

    this.setShoppingCartApplicationLogsRequest(applicationRequest);
    await this.updateApplication();

    const updatedApplicationRequest = await this.getApplicationLog(applicationRequest.sessionId ?? '').toPromise();
    applicationRequest.products = updatedApplicationRequest.products;
    applicationRequest.products?.map(({ accountType,  }) => {})

    this._setApplicationProductUUID(selectedAccounts, applicationRequest.products!);

    this.setShoppingCartApplicationLogsRequest(applicationRequest);
  }

  private _getOrderApplicantType(isJoint: boolean, order: boolean, orderForPrimany: boolean | undefined = undefined, orderForCoApplicant: boolean | undefined = undefined): number {
    if (!isJoint) {
      return order ? 1 : 0;
    }

    if (orderForPrimany && orderForCoApplicant) {
      return 3;
    }

    if (orderForPrimany && !orderForCoApplicant) {
      return 1;
    }

    if (!orderForPrimany && orderForCoApplicant) {
      return 2;
    }

    if (!orderForPrimany && !orderForCoApplicant) {
      return 1;
    }

    return 0;
  }

  private _setApplicationProductUUID(selectedAccounts: Array<SelectedAccount>, products: Array<ApplicationProduct>): void {
    const assignedUIDs: Array<string> = [];

    selectedAccounts.forEach(selectedAccount => {
      if (selectedAccount.applicationProductUID) {
        return;
      }

      const productMatch = products.find(product =>
        product.productCode === selectedAccount.accountCode &&
        product.orderChecks === selectedAccount.orderChecks &&
        product.orderDebitCard === selectedAccount.orderChecks &&
        !assignedUIDs.includes(product.applicationProductUID!)
      );

      if (!productMatch) {
        return;
      }

      selectedAccount.applicationProductUID = productMatch.applicationProductUID!;
      assignedUIDs.push(selectedAccount.applicationProductUID);
    })

    selectedAccounts.forEach(account => account.finished = true);

    const formValue = this.signupService.accountSelection.value;
    formValue.accounts = selectedAccounts;
    this.signupService.accountSelection.patchValue(formValue);
  }

  mapEnrolledAccounts(enrollmentResponse: EnrollmentResponse, linkedAccount: string | undefined): void {
    this.signupService.mapEnrolledAccounts(
      enrollmentResponse,
      this.getShoppingCartApplicationLogsRequest(),
      this._productConfiguration$.value,
      this.productSettings,
      linkedAccount
    )
  }

  accountAdded(account: Account): void {
    this.accountMediator.accountAdded(account);
  }

  accountEdited(previousAccount: string, newAccount: Account): void {
    this.accountMediator.accountEdited(previousAccount, newAccount);
  }

  accountDeleted(account: Account): void {
    this.accountMediator.accountDeleted(account);
  }

  resetAccountState(): void {
    this.accountMediator.resetAccountState();
  }

  accountSelectionHasBeenUpdated(): void {
    this._updatedAccountSelection$.next();
  }

  async getAndSetProductSettings(productUID: string): Promise<void> {
    if (!this.productSettings.get(productUID)) {
      const productSettings = await this.getBrandSettings(environment.app_settings_version, this.product!.brandId, productUID);
      const productSettingsMap: Map<string, unknown> = new Map();
      productSettings.forEach(setting => {
        const { name, value } = setting;
        if (!name && !value) { return; }
        productSettingsMap.set(name!, value);
      });

      this.productSettings.set(productUID, productSettingsMap);
    }

    this.accountMediator.updateAccountSettings(productUID, this.productSettings.get(productUID));
  }

  async initializeProductStateUponVerification(product: Product, applicationDetailLog: ApplicationDetailLog): Promise<void> {
    const { productUID, name, code, accountType, interestRate, allowJointApplications } = product;

    if (product.allowMultiProductApplications) {
      await this.getProductConfigurationByBrand(product);
    } else {
      this.signupService.setIfMultiProductAllowed(false)

      this.setProductConfiguration([{
        accountType: accountType,
        products: [{
          productUID,
          accountType,
          code,
          name,
          apy: interestRate,
          allowJointApplications
        }]
      }]);
    }

    const accounts = new FormArray([]);

    applicationDetailLog.products?.forEach(({ accountType, productUID, applicationProductUID, productCode}) => {
      const accountSelectionForm = SignupService.createAccountSelectionForm();
      accountSelectionForm.patchValue({
        accountType,
        account: productUID,
        accountCode: productCode,
        applicationProductUID: applicationProductUID,
        finished: true
      });

      accounts.push(accountSelectionForm);
    });

    this.signupService.addAccountSelectionForm(new FormGroup({
      accounts
    }));
  }
}
